import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, ExternalLink, Edit3, Globe, MessageSquare, CheckCircle, Lock, User, Phone, Mail, Share2, Copy } from "lucide-react";
import UnlockBar from "../components/UnlockBar";
import PublishNudge from "../components/PublishNudge";

/**
 * Simplified player dashboard (mobile-first).
 * 3 zones:
 *  1. STATE BANNER — "Publish $19" (unpaid) OR "You're live" (paid)
 *  2. YOUR PROFILE — single card with preview + 2 buttons (View / Edit)
 *  3. ACTIVITY (paid only) — enquiries
 *
 * The Account link lives in the navbar.
 */
export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Stripe return handling (preserve existing flow)
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    (async () => {
      let attempts = 0;
      while (attempts < 10) {
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success("Payment confirmed — your profile is now live!");
            if (refreshUser) await refreshUser();
            break;
          }
          if (data.status === "expired") { toast.error("Payment session expired"); break; }
        } catch {}
        await new Promise((r) => setTimeout(r, 2000));
        attempts++;
      }
    })();
  }, [searchParams, refreshUser]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: pd } = await api.get("/profile");
      setProfile(pd.profile);
      if (user?.plan) {
        try {
          const { data: ed } = await api.get("/profile/enquiries");
          setEnquiries(ed.enquiries || []);
        } catch {}
      }
    } catch {}
    setLoading(false);
  }, [user]);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  if (!user) return <div className="min-h-screen bg-[#0A0A0A]" />;

  const hasProfile = Boolean(profile && profile.generated_profile);
  const isPublished = profile?.status === "published";
  const isPaid = Boolean(user?.plan);
  const slug = profile?.slug;
  const profileUrl = slug && isPublished ? `${window.location.origin}/player/${slug}` : null;
  const firstName = (user.name || "").split(" ")[0] || "Player";

  const handleCopyLink = async () => {
    if (!profileUrl) return;
    try { await navigator.clipboard.writeText(profileUrl); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="player-dashboard">
      <UnlockBar />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 sm:py-12">

        {/* Greeting */}
        <div className="mb-7">
          <p className="text-[10px] text-[#D4AF37] tracking-[0.3em] uppercase mb-2">Dashboard</p>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-[#F5F5F5]">
            Hi {firstName}.
          </h1>
        </div>

        {/* ZONE 1 — STATE BANNER */}
        {!isPaid ? (
          <UnpaidBanner hasProfile={hasProfile} />
        ) : isPublished ? (
          <LiveBanner profileUrl={profileUrl} onCopy={handleCopyLink} />
        ) : hasProfile ? (
          <ReadyToPublishBanner onPublish={async () => {
            try {
              const { data } = await api.post("/profile/publish");
              toast.success("Published! You're now in the directory.");
              if (refreshUser) await refreshUser();
              loadData();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Couldn't publish — complete required fields in your profile first.");
            }
          }} />
        ) : null}

        {/* ZONE 2 — YOUR PROFILE CARD */}
        <ProfileCard
          loading={loading}
          hasProfile={hasProfile}
          isPublished={isPublished}
          profile={profile}
          profileUrl={profileUrl}
        />

        {/* ZONE 3 — ACTIVITY (paid only) */}
        {isPaid && hasProfile && (
          <ActivitySection enquiries={enquiries} viewCount={profile?.view_count || 0} />
        )}

        {/* Bottom links */}
        <div className="mt-10 pt-8 border-t border-[#1A1A1A] flex flex-wrap items-center justify-between gap-3 text-[11px]">
          <Link to="/account" className="flex items-center gap-1.5 text-[#888] hover:text-[#D4AF37] uppercase tracking-wider transition-colors" data-testid="dashboard-account-link">
            <User size={11} /> Account details
          </Link>
          <button
            onClick={async () => {
              if (!window.confirm("Reset your profile and start the onboarding chat again? This deletes your current profile data.")) return;
              try { await api.post("/onboarding/restart"); toast.success("Reset"); navigate("/onboarding"); } catch { toast.error("Failed"); }
            }}
            className="text-[#555] hover:text-[#A3A3A3] uppercase tracking-wider transition-colors"
            data-testid="dashboard-restart-link"
          >
            Restart onboarding
          </button>
        </div>
      </div>
      <PublishNudge />
    </div>
  );
}

/* ──────────────────────────── BANNERS ──────────────────────────── */

function UnpaidBanner({ hasProfile }) {
  return (
    <div className="relative mb-7 overflow-hidden rounded-md border border-[#D4AF37]/35 bg-gradient-to-br from-[#181207] via-[#0E0E0E] to-[#0A0A0A] p-5 sm:p-7" data-testid="publish-banner">
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#D4AF37]/[0.08] blur-[100px] pointer-events-none" />
      <div className="relative">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 mb-3">
          <Lock size={10} className="text-[#D4AF37]" />
          <span className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-[0.18em]">Not on the directory</span>
        </div>
        <h2 className="text-xl sm:text-2xl font-black tracking-tighter uppercase text-[#F5F5F5] leading-[1.1] mb-2">
          Your profile is <span className="text-[#D4AF37]">invisible to scouts</span>.
        </h2>
        <p className="text-sm text-[#9a9a9a] leading-relaxed mb-5">
          {hasProfile
            ? "Publish to the directory so scouts can find you and your link works publicly."
            : "Finish your profile first, then publish to the directory."}
        </p>
        <Link
          to="/paywall"
          data-testid="publish-banner-cta"
          className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-black tracking-widest uppercase text-xs sm:text-sm px-6 py-3.5 rounded-full hover:bg-[#E5C04A] active:scale-[0.98] transition-all"
        >
          Publish — $19 <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
        <p className="text-[10px] text-[#666] mt-2.5 tracking-wide">14-day refund · No subscription · One-off</p>
      </div>
    </div>
  );
}

function LiveBanner({ profileUrl, onCopy }) {
  return (
    <div className="relative mb-7 rounded-md border border-green-500/30 bg-gradient-to-br from-[#0a1a0a] via-[#0E0E0E] to-[#0A0A0A] p-5 sm:p-7" data-testid="live-banner">
      <div className="flex items-start gap-3 mb-4">
        <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
        <div>
          <h2 className="text-lg sm:text-xl font-black tracking-tighter uppercase text-[#F5F5F5] leading-tight">You're live in the directory.</h2>
          <p className="text-xs text-[#9a9a9a] leading-relaxed mt-1">Scouts can now find and contact you. Share your link anywhere.</p>
        </div>
      </div>
      {profileUrl && (
        <div className="flex flex-col sm:flex-row gap-2">
          <button onClick={onCopy} className="flex-1 flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-bold uppercase tracking-wider text-xs px-4 py-3 rounded-full hover:bg-[#E5C04A] transition-all" data-testid="dashboard-copy-link">
            <Copy size={12} /> Copy My Link
          </button>
          <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 border border-[#262626] text-[#E5E5E5] uppercase tracking-wider text-xs px-4 py-3 rounded-full hover:border-[#D4AF37]/40 transition-colors" data-testid="dashboard-view-public">
            <ExternalLink size={12} /> View Public Page
          </a>
        </div>
      )}
    </div>
  );
}

function ReadyToPublishBanner({ onPublish }) {
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    setBusy(true);
    try { await onPublish(); } finally { setBusy(false); }
  };
  return (
    <div className="mb-7 rounded-md border border-[#D4AF37]/35 bg-gradient-to-br from-[#181207] via-[#0E0E0E] to-[#0A0A0A] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="ready-to-publish-banner">
      <div className="min-w-0">
        <p className="text-sm text-[#F5F5F5] font-semibold mb-1">Profile ready to publish.</p>
        <p className="text-xs text-[#9a9a9a] leading-relaxed">Your payment is active — publish now to appear in the scout directory.</p>
      </div>
      <button
        onClick={handleClick}
        disabled={busy}
        className="flex-shrink-0 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-bold uppercase tracking-wider text-xs px-5 py-3 rounded-full hover:bg-[#E5C04A] active:scale-[0.98] transition-all disabled:opacity-50"
        data-testid="ready-to-publish-btn"
      >
        {busy ? "PUBLISHING..." : "PUBLISH NOW"} <ArrowRight size={12} />
      </button>
    </div>
  );
}

/* ──────────────────────────── PROFILE CARD ──────────────────────────── */

function ProfileCard({ loading, hasProfile, isPublished, profile, profileUrl }) {
  if (loading) {
    return (
      <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-md p-6 mb-6 flex justify-center" data-testid="profile-card-loading">
        <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!hasProfile) {
    return (
      <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-md p-6 mb-6 text-center" data-testid="profile-card-empty">
        <p className="text-[11px] text-[#D4AF37] tracking-[0.25em] uppercase mb-2">No profile yet</p>
        <h3 className="text-xl font-black text-[#F5F5F5] tracking-tight mb-2">Start with the AI chat.</h3>
        <p className="text-sm text-[#888] mb-5">A 10-minute chat builds your scout-ready profile.</p>
        <Link to="/onboarding" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-bold uppercase tracking-wider text-xs px-5 py-3 rounded-full hover:bg-[#E5C04A] transition-all" data-testid="dashboard-start-onboarding">
          Start now <ArrowRight size={12} />
        </Link>
      </div>
    );
  }

  const headline = profile?.generated_profile?.headline || profile?.edited_fields?.headline || "Player profile";
  const position = profile?.onboarding_data?.identity?.primary_position;
  const nationality = profile?.onboarding_data?.identity?.nationality;
  const name = profile?.onboarding_data?.identity?.full_name || profile?.name || "Your profile";

  return (
    <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-md overflow-hidden mb-6" data-testid="profile-card">
      <div className="p-5 sm:p-7">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] text-[#D4AF37] tracking-[0.25em] uppercase mb-1">Your profile</p>
            <h3 className="text-xl sm:text-2xl font-black tracking-tight text-[#F5F5F5] truncate">{name}</h3>
            <p className="text-sm text-[#888] mt-1 line-clamp-2">{headline}</p>
            <div className="flex flex-wrap items-center gap-2 mt-3 text-[10px] uppercase tracking-wider">
              {position && <span className="px-2 py-0.5 border border-[#262626] rounded-sm text-[#D4AF37]">{position}</span>}
              {nationality && <span className="px-2 py-0.5 border border-[#262626] rounded-sm text-[#888]">{nationality}</span>}
              <span className={`px-2 py-0.5 border rounded-sm ${isPublished ? "border-green-500/40 text-green-400" : "border-[#444] text-[#888]"}`}>
                {isPublished ? "PUBLISHED" : "DRAFT"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-5">
          <Link to="/profile/edit" className="flex-1 flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-bold uppercase tracking-wider text-xs px-4 py-3 rounded-full hover:bg-[#E5C04A] transition-all" data-testid="dashboard-edit-profile">
            <Edit3 size={12} /> Edit Profile
          </Link>
          {profileUrl ? (
            <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 border border-[#262626] text-[#E5E5E5] uppercase tracking-wider text-xs px-4 py-3 rounded-full hover:border-[#D4AF37]/40 transition-colors" data-testid="dashboard-view-profile">
              <ExternalLink size={12} /> View Public
            </a>
          ) : (
            <Link to="/preview" className="flex-1 flex items-center justify-center gap-2 border border-[#262626] text-[#E5E5E5] uppercase tracking-wider text-xs px-4 py-3 rounded-full hover:border-[#D4AF37]/40 transition-colors" data-testid="dashboard-preview-profile">
              <Globe size={12} /> Preview
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── ACTIVITY ──────────────────────────── */

function ActivitySection({ enquiries, viewCount }) {
  return (
    <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-md p-5 sm:p-7" data-testid="activity-section">
      <div className="flex items-center justify-between mb-5">
        <p className="text-[10px] text-[#D4AF37] tracking-[0.25em] uppercase">Activity</p>
        <span className="text-[10px] text-[#666]">Last 30 days</span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] p-4 rounded-sm" data-testid="stat-views">
          <p className="text-[9px] font-bold text-[#666] uppercase tracking-[0.2em] mb-2">Profile views</p>
          <p className="text-2xl font-black text-[#D4AF37] tabular-nums">{viewCount}</p>
        </div>
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] p-4 rounded-sm" data-testid="stat-enquiries">
          <p className="text-[9px] font-bold text-[#666] uppercase tracking-[0.2em] mb-2">Scout enquiries</p>
          <p className="text-2xl font-black text-[#D4AF37] tabular-nums">{enquiries.length}</p>
        </div>
      </div>

      {enquiries.length === 0 ? (
        <p className="text-xs text-[#555] text-center py-6">No enquiries yet — scouts will appear here when they reach out.</p>
      ) : (
        <ul className="space-y-3" data-testid="enquiries-list">
          {enquiries.slice(0, 5).map((e, i) => (
            <li key={i} className="bg-[#0A0A0A] border border-[#1A1A1A] p-4 rounded-sm">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-[#E5E5E5] truncate">{e.scout_name}</p>
                  <p className="text-[11px] text-[#888] truncate">{e.club} · {e.email}</p>
                </div>
                <span className="text-[10px] text-[#666] whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-xs text-[#A3A3A3] line-clamp-3 leading-relaxed">{e.message}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
